import {useHistory} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {apps, employeesIdName, loadings, openSelectEvidence, releaseData} from "../../../../store/slice";
import React, {useEffect, useState} from "react";
import {
    CreateCampaignReviewInstruction,
    GetAccessManagementApps,
    GetDataForRelease,
    GetEmployeesNameEmail
} from "../../../../store/actions";
import {
    GET_ALL_ACCESS_MANAGE_APPS_FOR_CREATE_AND_EDIT,
    PATH_ACCESS_MANAGEMENT_REVIEW_INSTRUCTIONS_EXACT,
    PATH_ACCESS_MANAGEMENT_REVIEW_INSTRUCTIONS_LIST,
    PATH_ACCESS_MANAGEMENT_REVIEW_INSTRUCTIONS_NEW,
    PATH_LOCAL_ACCESS_MANAGEMENT,
    SCHEDULE_TRIGGER_TYPE
} from "../../../../constants";
import {useForm} from "../../../../../../../newShared/hooks/useForm";
import {campaignReviewInstructionType, evidenceWithNames, onlyActor} from "../../../../types";
import {getInitialInstruction, mapper} from "../../../../helpers";
import {useMainTranslation} from "../../../../../../../newShared/hooks/useMainTranslationHooks/useMainTranslation";
import {useSetBreadcrumbs} from "../../../../../../barsEnvironment/breadcrumbs/hooks/useBreadcrumbs";
import {translateText} from "../../../../../automations/helpers";
import {
    UseManageWorkspacesAndOrganizations
} from "../../../../../../authWorkspacesCookies/cookies/hooks/useManageWorkspacesAndOrganizations";
import {useMessageDialog} from "../../../../../../barsEnvironment/MessageDialog/hooks/useMessageDialog";
import {validateEmail} from "../../../../../../../newShared/utils/text";

export const useCampaignReviewInstructionCreate = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const {currData} = UseManageWorkspacesAndOrganizations();
    const {t: tMenu} = useMainTranslation('', {keyPrefix: 'LeftMenu'});
    const {setMessage} = useMessageDialog();
    const _apps = useSelector(apps);
    const releaseDT = useSelector(releaseData);
    const empls = useSelector(employeesIdName);
    //controller
    const controller = new AbortController();

    //useEffects
    useEffect(() => {
        !empls.length && dispatch(GetEmployeesNameEmail());
        !_apps.length && dispatch(GetAccessManagementApps({data: {page: 0, count: GET_ALL_ACCESS_MANAGE_APPS_FOR_CREATE_AND_EDIT}, signal: controller.signal})); //gettings all apps
        (!releaseDT.frameworks.length) && dispatch(GetDataForRelease(controller.signal)); //getting frameworks/controls/evidences and employees
        setExpands(defaultExpands);
        const translateText1 = translateText(form.trigger.repeat);
        setScheduleSelect({value: translateText1.timeValue, num: translateText1.factor});
        //eslint-disable-next-line
    }, []);

    useSetBreadcrumbs([
        {
            title: tMenu('Access management'),
            path: PATH_LOCAL_ACCESS_MANAGEMENT
        },
        {
            title: tMenu('Review campaign config'),
            path: PATH_ACCESS_MANAGEMENT_REVIEW_INSTRUCTIONS_LIST
        },
        {
            title: tMenu('New configuration'),
            path: PATH_ACCESS_MANAGEMENT_REVIEW_INSTRUCTIONS_NEW
        }
    ]);


    //selectors
    const {campaignReviewInstructionsNew, dataForRelease, list, getEmployees} = useSelector(loadings);

    //form
    const {form, setForm, handleChange} = useForm<campaignReviewInstructionType>(getInitialInstruction());

    const handleChangeEtaConfig = (event:React.ChangeEvent<HTMLInputElement>) => {
        event.persist();
        setForm((prevValues) => ({
            ...prevValues,
            etaConfig: {
                ...prevValues.etaConfig,
                [event.target.name]: event.target.value,
            }
        }));
    };

    //actions
    const handleGoBack = () => history.push(PATH_ACCESS_MANAGEMENT_REVIEW_INSTRUCTIONS_LIST);

    const handleSubmit = () => {
        if(isOk()){
            //eta filled by number of days -> translating to ms
            const requestForm: campaignReviewInstructionType = {
                ...form,
                etaConfig: {
                    etaManager: form.etaConfig.etaManager * mapper.Days,
                    etaVendor: form.etaConfig.etaVendor * mapper.Days,
                    etaExecution: form.etaConfig.etaExecution * mapper.Days,
                    etaVerify: form.etaConfig.etaVerify * mapper.Days,
                    etaSign: form.etaConfig.etaSign * mapper.Days,
                },
                workspaceId: currData?.selectedWorkspace?.id ?? '',
                workspaceName: currData?.selectedWorkspace?.name ?? '',
                evidence: {
                    type: 'evidence',
                    data: form.evidences.map(e => {return {id: e.evidenceId, name: e.evidenceName ?? '', controlId: e.controlId, frameworkId: e.frameworkId}})
                },
                applicationAccessIds: form.applications.map(e => e.id),
            }

            dispatch(CreateCampaignReviewInstruction({data: {instruction: requestForm, onSuccess: onSuccessCreate}}));
        }
    }

    const onSuccessCreate = (_id: string) => {
        history.push(PATH_ACCESS_MANAGEMENT_REVIEW_INSTRUCTIONS_EXACT.replace(':id', _id));
        setMessage({title: 'Completed successfully!', message: 'Configuration created!'});
    }

    const isOk = (): boolean => {
        return (
            form.name.trim().length > 0 &&
                form.etaConfig.etaManager > 0 &&
                form.etaConfig.etaVendor > 0 &&
                form.etaConfig.etaExecution > 0 &&
                form.etaConfig.etaVerify > 0 &&
                form.etaConfig.etaSign > 0 &&
                form.evidences.length > 0 &&
                form.applications.length > 0 &&
                !isCommonExecutionTimeIsBiggerThanRepeat() &&
                validateEmail(form.finalApprover.email)
        )
    }

    //expands
    type expandsType = {
        info: boolean;
        eta: boolean;
        evidence: boolean;
        apps: boolean;
        schedule: boolean;
        finalApprover: boolean;
    }
    const defaultExpands: expandsType = {
        info: true,
        eta: false,
        evidence: false,
        apps: false,
        schedule: false,
        finalApprover: false
    }
    const [_expands, setExpands] = useState<expandsType>(defaultExpands);

    const toggleExpand = (expand: keyof typeof _expands) => {
        setExpands({..._expands, [expand]: !_expands[expand]});
    }

    const handleDeleteEvidence = (id: string) => {
        setForm({...form, evidences: [...form.evidences.filter(e => e.evidenceId !== id)]});
    }

    const handleOpenAddEvidence = () => {
        dispatch(openSelectEvidence());
    }

    const handleSelectEvidence = (evidence: evidenceWithNames) => {
        setForm({...form, evidences: [evidence, ...form.evidences]})
    }

    const handleAddApp = (id: string, name: string) => {
        setForm({...form, applications: [...form.applications, {id, name}]});
    }

    const handleDeleteApp = (id: string) => {
        setForm({...form, applications: [...form.applications.filter(e => e.id !== id)]});
    }

    const handleSetScheduleType = (event: React.ChangeEvent<HTMLInputElement>) => {
        setForm({...form, trigger: {...form.trigger, type: (event.target as HTMLInputElement).value}})
    }

    const [scheduleSelect, setScheduleSelect] = useState<{value: string, num: number}>({value: 'Years', num: 1});

    const handleSelectRepeatNum = (num: number) => {
        const inSeconds = num*mapper[scheduleSelect.value]; //translated to miliseconds already
        setForm({...form, trigger: {...form.trigger, repeat: inSeconds}});
        setScheduleSelect({...scheduleSelect, num});
    }

    const handleSelectRepeatValue = (value: string) => {
        const inSeconds = scheduleSelect.num*mapper[value]; //translated to miliseconds already
        setForm({...form, trigger: {...form.trigger, repeat: inSeconds}});
        setScheduleSelect({...scheduleSelect, value});

    }

    const handleSetStartTrigger = (value: string | null) => {
        if(value){
            const normalizedValue = new Date(value).toISOString();
            // console.log(`handleSetStartTrigger: ${value} -> ${normalizedValue}`);
            value && setForm({...form, trigger: {...form.trigger, start: normalizedValue}});
        }
    }

    const handleSetFinalApprover = (actor: onlyActor) => {
        setForm({...form, finalApprover: {
                firstName: actor.firstName,
                lastName: actor.lastName,
                publicId: actor.publicId,
                email: actor.email ?? ''
            }});
    }

    const handleChangeFinalApprover = (email: string) => {
        setForm({...form, finalApprover: {email, lastName: null, firstName: null, publicId: null }});
    }

    //
    const isCommonExecutionTimeIsBiggerThanRepeat = (): boolean => {
        // console.log(`isCommonExecutionTimeIsBiggerThanRepeat: ${form.trigger.repeat} < ${ (Object.values(form.etaConfig).reduce((a, b) => a + b, 0) * mapper.Days)} === ${form.trigger.repeat > (Object.values(form.etaConfig).reduce((a, b) => a + b, 0) * mapper.Days)} \n
        //  commonDays: ${(Object.values(form.etaConfig).reduce((a, b) => a + b, 0))}
        //  etaConfig: ${Object.values(form.etaConfig)}
        //  `);
        return (form.trigger.type === SCHEDULE_TRIGGER_TYPE) ? form.trigger.repeat < (Object.values(form.etaConfig).reduce((a, b) => a + b, 0) * mapper.Days) : false;
    }

    //const finalApproverSelectionType
    const [finalApproverSelectionType, setFinalApproverSelectionType] = useState<'email' | 'hr'>('hr');

    return{
        releaseDT,
        _apps,
        loading: {
            isLoadingCreate: campaignReviewInstructionsNew,
            isLoadingDataForCreate: dataForRelease,
            isLoadingApps: list,
        },
        actions: {
            handleGoBack,
            handleSubmit,
            isOk,
            handleDeleteEvidence,
            handleOpenAddEvidence,
            handleSelectEvidence,

            handleAddApp,
            handleDeleteApp,
            handleSetScheduleType,
            handleSetStartTrigger,
            handleSetFinalApprover,
            handleChangeFinalApprover,
            setFinalApproverSelectionType: (type: string) => {
                setFinalApproverSelectionType(type as ("email" | "hr"));
                setForm({...form, finalApprover: {email: '', publicId: null, firstName: null, lastName: null}});
            }
        },
        finalApproverSelectionType,
        form,
        handleChange,
        handleChangeEtaConfig,
        _expands,
        toggleExpand,
        translatedRepeat: scheduleSelect,
        handleSelectRepeatNum,
        handleSelectRepeatValue,
        isCommonExecutionTimeIsBiggerThanRepeat: isCommonExecutionTimeIsBiggerThanRepeat(),
        employees: empls,
        isLoadingEmployees: getEmployees,
    }
}
